import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { DeleteOutline, Download, Public, Refresh, ReceiptLong } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { downloadBlob, errMsg, inr, num } from './common';
import DistributorZonesDialog from './DistributorZonesDialog';

interface Props {
  run: any;
  onChanged: () => Promise<void> | void;
  onGenerated: () => void;
}

const TIER_SHORT: Record<string, string> = {
  '1': 'T1 last-5', '2': 'T2 this store', '3': 'T3 all stores', '4': 'T4 sole', '4.5': 'name match', '5': 'mfr fallback',
  '6': 'other zone', '7': 'other-zone mfr',
};

const DistributorStep: React.FC<Props> = ({ run, onChanged, onGenerated }) => {
  const [distributors, setDistributors] = useState<Array<{ id: number; distributor_name: string }>>([]);
  const [hints, setHints] = useState<Record<string, any>>({});
  const [tierNames, setTierNames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otherFor, setOtherFor] = useState<number | null>(null);
  const [otherZones, setOtherZones] = useState(false);
  const [zonesOpen, setZonesOpen] = useState(false);

  const items = useMemo(() => run.items.filter((i: any) => i.decision === 'accepted' && Number(i.approved_qty) > 0), [run.items]);
  const assigned = run.status === 'distributors_assigned';
  const distName = useMemo(() => new Map(distributors.map((d) => [d.id, d.distributor_name])), [distributors]);

  useEffect(() => { orderProcessorAPI.distributors().then(setDistributors).catch(() => undefined); }, []);
  useEffect(() => {
    if (!assigned) return;
    orderProcessorAPI.distributorHints(run.id).then((r) => {
      const m: Record<string, any> = {};
      r.hints.forEach((h: any) => { m[`${h.manufacturer_id}:${h.distributor_id}`] = h; });
      setHints(m); setTierNames(r.tiers);
    }).catch(() => undefined);
  }, [run.id, assigned, run.updated_at]);

  const hintFor = (item: any, did: number | null) => {
    if (!did) return null;
    const ids: number[] = item.member_ids?.length ? item.member_ids : [item.manufacturer_id];
    const found = ids.map((mid) => hints[`${mid}:${did}`]).filter(Boolean);
    return found.sort((a, b) => String(b.last_date).localeCompare(String(a.last_date)))[0] || null;
  };

  const assign = async () => {
    setBusy('assign'); setError(null);
    try {
      if (run.status === 'draft') await orderProcessorAPI.setRunStatus(run.id, 'reviewed');
      await orderProcessorAPI.assignDistributors(run.id, otherZones);
      await onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  const choose = async (item: any, did: number) => {
    setBusy(`item-${item.id}`); setError(null);
    try { await orderProcessorAPI.overrideDistributor(run.id, item.id, did); await onChanged(); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); setOtherFor(null); }
  };

  // Remove = reject the item (decision 'rejected'); it drops out of the POs and can be accepted again in the Review step
  const remove = async (item: any) => {
    if (!window.confirm(`Remove “${item.medicine_name}” from this order? It will not be on any PO (you can accept it again in Review).`)) return;
    setBusy(`item-${item.id}`); setError(null);
    try {
      await orderProcessorAPI.updateItems(run.id, [{ id: item.id, decision: 'rejected', reject_reason: 'Removed at distributor step' }]);
      await onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  const generate = async () => {
    setBusy('po'); setError(null);
    try { await orderProcessorAPI.createPurchaseOrders(run.id); await onChanged(); onGenerated(); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  const summary = useMemo(() => {
    const m = new Map<number | null, { count: number; strips: number; value: number }>();
    items.forEach((i: any) => {
      const k = i.selected_distributor_id ?? null;
      const s = m.get(k) || { count: 0, strips: 0, value: 0 };
      s.count += 1; s.strips += Number(i.strip_qty || 0); s.value += Number(i.approved_qty) * Number(i.landing_price || 0);
      m.set(k, s);
    });
    return Array.from(m.entries()).sort((a, b) => b[1].count - a[1].count);
  }, [items]);
  const missing = items.filter((i: any) => !i.selected_distributor_id);

  const zoneControls = (
    <>
      <FormControlLabel sx={{ ml: 0 }} disabled={!!busy}
        control={<Checkbox size="small" checked={otherZones} onChange={(e) => setOtherZones(e.target.checked)} />}
        label={<Typography variant="body2">Include other zones (may auto-pick distributors outside this zone)</Typography>} />
      <Button size="small" startIcon={<Public />} onClick={() => setZonesOpen(true)}>Distributor zones</Button>
    </>
  );

  if (!assigned) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          {items.length} accepted item(s). This computes strip quantity from pack size and ranks the top 3 distributors per item
          using the PO Request rules (this store’s last-5 purchases → this store 6 months → all stores’ purchases → sole supplier → medicine-name → manufacturer fallback).
          Only distributors in this store’s zone are picked automatically; distributors outside the zone (48 h+ delivery) are listed as options to choose by hand.
          Tick the box to also suggest other FirstShield zones and let the best-ranked distributor be picked even when it is outside the zone.
        </Alert>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={assign} disabled={!!busy || !items.length} startIcon={busy === 'assign' ? <CircularProgress size={16} /> : undefined}>
            Assign distributors
          </Button>
          {zoneControls}
        </Stack>
        <DistributorZonesDialog open={zonesOpen} onClose={() => setZonesOpen(false)} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>PO preview by distributor</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {summary.map(([did, s]) => (
            <Chip key={String(did)} color={did ? 'default' : 'error'}
              label={`${did ? distName.get(did) || `#${did}` : 'No distributor'} · ${s.count} items · ${num(s.strips, 0)} strips · ${inr(s.value)}`} />
          ))}
        </Stack>
      </Paper>
      {missing.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{missing.length} item(s) have no distributor (“Not Found” in the old tool) — pick one before generating POs.</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" startIcon={<Refresh />} onClick={assign} disabled={!!busy}>Re-run ranking</Button>
        {zoneControls}
        <Button size="small" startIcon={<Download />} onClick={async () => {
          try { downloadBlob(await orderProcessorAPI.exportRunCsv(run.id, 'po-request'), `${run.run_number}_po_request_final.csv`); } catch (e) { setError(errMsg(e)); }
        }}>PO request CSV</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Medicine</TableCell>
              <TableCell align="right">Loose qty</TableCell>
              <TableCell>Pack</TableCell>
              <TableCell align="right">Strips</TableCell>
              <TableCell>Distributor options (up to 5, best first)</TableCell>
              <TableCell sx={{ minWidth: 260 }}>Order from</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i: any) => {
              // v2: up to 5 de-duplicated candidates (duplicate masters folded); older runs fall back to distributor1–3
              const cands: any[] = Array.isArray(i.distributor_candidates) && i.distributor_candidates.length
                ? i.distributor_candidates
                : [i.distributor1_id, i.distributor2_id, i.distributor3_id].map((d, idx) => (d ? { distributor_id: d, tier: i.distributor_tiers?.[idx] } : null))
                  .filter((c, idx, arr) => c && arr.findIndex((x: any) => x && x.distributor_id === c.distributor_id) === idx);
              const opts = Array.from(new Set(cands.map((c: any) => c.distributor_id).concat(i.selected_distributor_id ? [i.selected_distributor_id] : [])));
              return (
                <TableRow key={i.id} hover sx={{ bgcolor: i.selected_distributor_id ? undefined : '#fff4f4' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{i.medicine_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{i.manufacturer_name}</Typography>
                    {i.in_customer_orders && (
                      <Chip size="small" color={i.in_optimizer && Number(i.optimizer_qty) > 0 ? 'warning' : 'info'} variant="outlined" sx={{ ml: 0.5 }}
                        label={`${num(i.customer_order_count, 0)} customer order(s)`} />
                    )}
                  </TableCell>
                  <TableCell align="right">{num(i.approved_qty, 0)}</TableCell>
                  <TableCell>{i.pack_size || '—'}</TableCell>
                  <TableCell align="right"><b>{num(i.strip_qty, 0)}</b></TableCell>
                  <TableCell>
                    {!cands.length && <Typography variant="caption" color="error">Not Found — pick “Other distributor…”</Typography>}
                    {cands.length > 0 && !i.selected_distributor_id && (
                      <Typography variant="caption" color="error" display="block">No distributor in this store’s zone — choose one below (outside the zone takes 48 h+)</Typography>
                    )}
                    {cands.map((c: any, idx: number) => {
                      const d = c.distributor_id;
                      const h = c.last_date ? { last_date: c.last_date, ptr_per_unit: c.last_ptr, discount_pct: c.last_discount_pct } : hintFor(i, d);
                      const tier = c.tier;
                      return (
                        <Typography key={d} variant="caption" display="block" sx={{ fontWeight: d === i.selected_distributor_id ? 700 : 400 }}>
                          {idx + 1}. {c.name || distName.get(d) || `#${d}`}
                          {c.alias_ids?.length > 1 && <Tooltip title={`Duplicate masters merged: ${c.alias_ids.map((a: number) => distName.get(a) || a).join(' / ')}`}><span> ⧉</span></Tooltip>}
                          {tier !== null && tier !== undefined && (
                            <Tooltip title={tierNames[String(Number(tier))] || c.tier_name || ''}><span> · <i>{TIER_SHORT[String(Number(tier))] || `tier ${tier}`}</i></span></Tooltip>
                          )}
                          {c.own_zone === false && (
                            <Tooltip title={c.zone_id === 999 ? 'Located outside all FirstShield zones — delivery usually 48 h+' : 'Located in another FirstShield zone — delivery usually 48 h+'}>
                              <span style={{ color: '#b26a00' }}> · {c.zone_id === 999 ? 'outside zone' : 'other FS zone'}</span>
                            </Tooltip>
                          )}
                          {c.avg_discount_pct !== null && c.avg_discount_pct !== undefined && <span style={{ color: '#666' }}> · avg {num(c.avg_discount_pct, 1)}%</span>}
                          {h && <span style={{ color: '#666' }}> · last {h.last_date} PTR {num(h.ptr_per_unit)} · {num(h.discount_pct, 1)}%</span>}
                        </Typography>
                      );
                    })}
                  </TableCell>
                  <TableCell>
                    {otherFor === i.id ? (
                      <Autocomplete size="small" options={distributors} getOptionLabel={(o) => o.distributor_name}
                        onChange={(_, v) => { if (v) choose(i, v.id); }} onBlur={() => setOtherFor(null)}
                        renderInput={(p) => <TextField {...p} autoFocus placeholder="Search distributor" />} />
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Select size="small" fullWidth displayEmpty value={i.selected_distributor_id ?? ''} disabled={busy === `item-${i.id}`}
                          onChange={(e) => { const v = e.target.value as any; if (v === 'other') setOtherFor(i.id); else if (v) choose(i, Number(v)); }}>
                          <MenuItem value="" disabled><em>Choose distributor</em></MenuItem>
                          {opts.map((d: any) => <MenuItem key={d} value={d}>{distName.get(d) || `#${d}`}</MenuItem>)}
                          <MenuItem value="other">Other distributor…</MenuItem>
                        </Select>
                        {i.distributor_overridden && <Chip size="small" color="info" label="changed" />}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Tooltip title="Remove from this order">
                      <span>
                        <IconButton size="small" color="error" disabled={!!busy} onClick={() => remove(i)}><DeleteOutline fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" color="primary" disabled={!!busy || missing.length > 0} onClick={generate}
          startIcon={busy === 'po' ? <CircularProgress size={16} /> : <ReceiptLong />}>
          Generate {summary.filter(([d]) => d).length} purchase order(s)
        </Button>
      </Box>
      <DistributorZonesDialog open={zonesOpen} onClose={() => setZonesOpen(false)} />
    </Box>
  );
};

export default DistributorStep;
